import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import { useAuth } from '../contexts/AuthContext';
import { buildInfo } from '../config/buildInfo';
import {
  APP_UPDATE_PENDING_CHECK_KEY,
  APP_VERSION_CHECK_PENDING_EVENT,
  PWA_MAIN_SCREEN_PATHS,
} from '../constants/pwaInstallPrompt';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';
import {
  activateWaitingWorker,
  checkForNewBuild,
  fetchServerBuildNumber,
  getClientBuildNumber,
  hardReloadApp,
  reloadOnControllerChange,
  VersionCheckResult,
} from '../utils/appVersionCheck';

const UPDATE_PROMPT_COOLDOWN_MS = 10 * 60 * 1000;
const UPDATE_DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const UPDATE_COOLDOWN_KEY = 'appUpdatePromptCooldownUntil';
const UPDATE_DISMISS_KEY = 'appUpdateDismissedBuild';
const UPDATE_SUCCESS_FLAG = 'appShowUpdateSuccess';
const APP_LAST_APPLIED_BUILD_KEY = 'appLastAppliedBuild';
const INITIAL_CHECK_DELAY_MS = 1500;
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

const AppUpdatePrompt: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const isMainScreen = PWA_MAIN_SCREEN_PATHS.includes(pathname);

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorkerRegistration | null>(
    null
  );
  const [detectedServerBuild, setDetectedServerBuild] = useState<number | null>(null);
  const [postUpdateVersion, setPostUpdateVersion] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const checkInFlightRef = useRef(false);

  const shouldShowUpdatePrompt = useCallback(async (): Promise<boolean> => {
    if (localStorage.getItem('appIsUpdating')) {
      return false;
    }

    const cooldownUntil = sessionStorage.getItem(UPDATE_COOLDOWN_KEY);
    if (cooldownUntil && Date.now() < Number(cooldownUntil)) {
      return false;
    }

    const dismissedRaw = localStorage.getItem(UPDATE_DISMISS_KEY);
    if (dismissedRaw) {
      try {
        const { build: dismissedServerBuild, until } = JSON.parse(dismissedRaw) as {
          build: number;
          until: number;
        };
        if (Date.now() < until) {
          const currentServerBuild = await fetchServerBuildNumber();
          if (
            currentServerBuild != null &&
            currentServerBuild <= dismissedServerBuild
          ) {
            return false;
          }
        }
      } catch {
        localStorage.removeItem(UPDATE_DISMISS_KEY);
      }
    }

    return true;
  }, []);

  const silentActivateWaiting = useCallback(
    (registration: ServiceWorkerRegistration) => {
      if (!registration.waiting) {
        return;
      }
      if (activateWaitingWorker(registration)) {
        reloadOnControllerChange();
      }
    },
    []
  );

  const applyUpdateCheckResult = useCallback(
    async (
      result: VersionCheckResult,
      registration: ServiceWorkerRegistration | null
    ) => {
      if (result.action === 'prompt_update') {
        setDetectedServerBuild(result.serverBuild);
        setUpdateAvailable(true);
        setWaitingWorker(registration);
        return;
      }

      if (result.action === 'silent_activate' && registration?.waiting) {
        silentActivateWaiting(registration);
      }
    },
    [silentActivateWaiting]
  );

  const runVersionCheck = useCallback(async () => {
    if (checkInFlightRef.current) {
      return;
    }

    if (!(await shouldShowUpdatePrompt())) {
      return;
    }

    checkInFlightRef.current = true;
    try {
      let registration: ServiceWorkerRegistration | null = null;
      if ('serviceWorker' in navigator) {
        registration = (await navigator.serviceWorker.getRegistration()) ?? null;
      }

      serviceWorkerRegistration.checkForUpdates();

      const result = await checkForNewBuild();
      await applyUpdateCheckResult(result, registration);
    } catch (error) {
      console.error('Error al verificar versión para actualización:', error);
    } finally {
      checkInFlightRef.current = false;
      sessionStorage.removeItem(APP_UPDATE_PENDING_CHECK_KEY);
    }
  }, [applyUpdateCheckResult, shouldShowUpdatePrompt]);

  useEffect(() => {
    if (localStorage.getItem('appIsUpdating')) {
      setIsUpdating(true);
      const timer = setTimeout(() => {
        localStorage.removeItem('appIsUpdating');
        setIsUpdating(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (localStorage.getItem(UPDATE_SUCCESS_FLAG)) {
      localStorage.removeItem(UPDATE_SUCCESS_FLAG);
      localStorage.removeItem('appUpdatedToVersion');
      localStorage.removeItem(UPDATE_DISMISS_KEY);
      localStorage.setItem(
        APP_LAST_APPLIED_BUILD_KEY,
        String(buildInfo.buildNumber)
      );
      sessionStorage.setItem(
        UPDATE_COOLDOWN_KEY,
        String(Date.now() + UPDATE_PROMPT_COOLDOWN_MS)
      );
      setPostUpdateVersion(
        `v${buildInfo.version} (build ${buildInfo.buildNumber})`
      );
      return;
    }

    const legacyVersion = localStorage.getItem('appUpdatedToVersion');
    if (legacyVersion) {
      setPostUpdateVersion(legacyVersion);
      localStorage.removeItem('appUpdatedToVersion');
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isMainScreen || process.env.NODE_ENV === 'development') {
      return undefined;
    }

    const initialTimer = setTimeout(() => {
      void runVersionCheck();
    }, INITIAL_CHECK_DELAY_MS);

    const interval = setInterval(() => {
      void runVersionCheck();
    }, CHECK_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [isAuthenticated, isMainScreen, runVersionCheck]);

  useEffect(() => {
    if (
      !isAuthenticated ||
      !isMainScreen ||
      process.env.NODE_ENV === 'development'
    ) {
      return undefined;
    }

    const handlePendingCheck = () => {
      void runVersionCheck();
    };

    window.addEventListener(APP_VERSION_CHECK_PENDING_EVENT, handlePendingCheck);

    if (sessionStorage.getItem(APP_UPDATE_PENDING_CHECK_KEY) === '1') {
      void runVersionCheck();
    }

    return () => {
      window.removeEventListener(APP_VERSION_CHECK_PENDING_EVENT, handlePendingCheck);
    };
  }, [isAuthenticated, isMainScreen, pathname, runVersionCheck]);

  const handleUpdateDismiss = () => {
    setUpdateAvailable(false);
    setWaitingWorker(null);
    localStorage.setItem(
      UPDATE_DISMISS_KEY,
      JSON.stringify({
        build: detectedServerBuild ?? getClientBuildNumber(),
        until: Date.now() + UPDATE_DISMISS_COOLDOWN_MS,
      })
    );
  };

  const handleUpdateClick = async () => {
    setIsUpdating(true);
    setUpdateAvailable(false);
    localStorage.setItem('appIsUpdating', 'true');

    let showSuccessMessage = true;
    try {
      const result = await checkForNewBuild();
      showSuccessMessage = result.action === 'prompt_update';
    } catch (error) {
      console.error('Error al verificar versión antes de actualizar:', error);
    }

    if (showSuccessMessage) {
      localStorage.setItem(UPDATE_SUCCESS_FLAG, '1');
      localStorage.removeItem(UPDATE_DISMISS_KEY);
    }

    await hardReloadApp(waitingWorker);
  };

  const handlePostUpdateClose = () => {
    setPostUpdateVersion(null);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <Snackbar
        open={updateAvailable && !isUpdating && isMainScreen}
        message="Hay una nueva versión disponible"
        action={
          <Button color="primary" size="small" onClick={handleUpdateClick}>
            Actualizar
          </Button>
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleUpdateDismiss}
      />
      <Snackbar
        open={Boolean(postUpdateVersion)}
        message={`Aplicación actualizada a ${postUpdateVersion}`}
        autoHideDuration={6000}
        onClose={handlePostUpdateClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </>
  );
};

export default AppUpdatePrompt;
